import { CustomAttribute } from 'aurelia';

/**
 * Gentle "water surface" ripples that follow the mouse.
 *
 * A full-viewport <canvas> is placed behind the app content (pointer-events:
 * none, negative z-index) and, as the cursor travels, drops soft expanding
 * rings that fade out — a subtle reflective-water touch over the Cosmos
 * background. Kept deliberately low-key: low opacity, thin rings, capped count.
 *
 * Skips entirely when the user prefers reduced motion or has no fine pointer
 * (e.g. touch-only devices), and cleans everything up on detach. The animation
 * loop idles (stops requesting frames) whenever there are no live ripples.
 */
export class WaterRippleAttr {
  // --- gentle-by-design tuning knobs ---------------------------------------
  private static readonly MAX_RIPPLES = 30;       // hard cap on live ripples (bounds cost)
  private static readonly SPAWN_DISTANCE = 25;    // px the cursor must travel to drop a new ripple
  private static readonly LIFETIME = 1100;        // ms for a ripple to expand + fade away
  private static readonly MAX_RADIUS = 120;       // px final radius of a ripple
  private static readonly START_ALPHA = 0.15;     // peak opacity (low = subtle)
  private static readonly LINE_WIDTH = 2.6;       // ring thickness in px (wider reads softer once blurred)
  private static readonly BLUR = 8;               // px CSS blur on the canvas — melts rings into water-like warps
  private static readonly TINT = '170, 214, 255'; // cool water-blue (r, g, b)
  // -------------------------------------------------------------------------

  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private ripples: { x: number; y: number; born: number }[] = [];
  private width = 0;
  private height = 0;
  private dpr = 1;
  private rafId = 0;
  private running = false;
  private lastX = 0;
  private lastY = 0;
  private hasLast = false;

  private readonly onMove = (e: MouseEvent): void => this.handleMove(e.clientX, e.clientY);
  private readonly onResize = (): void => this.resize();

  attached(): void {
    // Respect reduced-motion, and skip devices without a fine pointer (touch).
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia?.('(pointer: fine)').matches;
    if (reduce || !finePointer) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'water-ripple-canvas';
    const s = canvas.style;
    s.position = 'fixed';
    s.top = '0';
    s.left = '0';
    s.width = '100%';
    s.height = '100%';
    s.zIndex = '-1';
    s.pointerEvents = 'none';
    // The full-canvas CSS blur is what melts the rings into the water-like warp,
    // so it's kept. Cost is reduced instead by backing the canvas at dpr=1 (see
    // resize()) rather than dropping the blur.
    s.filter = `blur(${WaterRippleAttr.BLUR}px)`;
    document.body.appendChild(canvas);

    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) {
      canvas.remove();
      this.canvas = null;
      return;
    }

    this.resize();
    window.addEventListener('mousemove', this.onMove, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  detaching(): void {
    window.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('resize', this.onResize);
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.rafId = 0;
    this.running = false;
    this.ripples = [];
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
  }

  private resize(): void {
    if (!this.canvas || !this.ctx) {
      return;
    }
    // Draw in CSS pixels, back the canvas at device resolution (capped for perf).
    this.width = window.innerWidth;
    this.height = window.innerHeight;
    // PERF: clamp DPR to 1 for this decorative layer. Combined with the CSS blur
    // (which melts the rings anyway), backing at 1x is visually near-identical while
    // roughly halving per-frame fill and blur cost on high-DPI screens. Original:
    // this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.dpr = 1;
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  private handleMove(x: number, y: number): void {
    if (!this.hasLast) {
      this.lastX = x;
      this.lastY = y;
      this.hasLast = true;
      return;
    }

    // Only drop a ripple once the cursor has travelled far enough — this both
    // spaces the rings out and keeps the ripple count naturally low.
    const dx = x - this.lastX;
    const dy = y - this.lastY;
    const spawnDist = WaterRippleAttr.SPAWN_DISTANCE;
    if (dx * dx + dy * dy < spawnDist * spawnDist) {
      return;
    }

    this.lastX = x;
    this.lastY = y;
    this.ripples.push({ x, y, born: performance.now() });
    if (this.ripples.length > WaterRippleAttr.MAX_RIPPLES) {
      this.ripples.shift();
    }

    if (!this.running) {
      this.running = true;
      this.rafId = requestAnimationFrame(this.frame);
    }
  }

  private readonly frame = (now: number): void => {
    const ctx = this.ctx;
    if (!ctx) {
      this.running = false;
      return;
    }

    ctx.clearRect(0, 0, this.width, this.height);
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = WaterRippleAttr.LINE_WIDTH;

    const lifetime = WaterRippleAttr.LIFETIME;
    const maxRadius = WaterRippleAttr.MAX_RADIUS;
    const startAlpha = WaterRippleAttr.START_ALPHA;
    const tint = WaterRippleAttr.TINT;

    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const ripple = this.ripples[i];
      const age = (now - ripple.born) / lifetime;
      if (age >= 1) {
        this.ripples.splice(i, 1);
        continue;
      }

      // The rAF frame-start timestamp can be marginally earlier than the
      // performance.now() captured when the ripple was spawned in the pointer
      // handler, which would make `age` slightly negative and the radius below
      // negative (arc() rejects negative radii). Clamp to keep it non-negative.
      const t = age < 0 ? 0 : age;
      const ease = 1 - Math.pow(1 - t, 3); // ease-out expansion
      const radius = maxRadius * ease;
      const alpha = startAlpha * (1 - t);  // fade out over its life

      ctx.beginPath();
      ctx.strokeStyle = `rgba(${tint}, ${alpha})`;
      ctx.arc(ripple.x, ripple.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.globalCompositeOperation = 'source-over';

    if (this.ripples.length > 0) {
      this.rafId = requestAnimationFrame(this.frame);
    } else {
      this.running = false;
      this.rafId = 0;
    }
  };
}

export const WaterRipple = CustomAttribute.define({ name: 'water-ripple' }, WaterRippleAttr);
